import type { Dayjs } from "dayjs";

import {
    shortMonthDayAndYearWithDayOfWeek,
    dayAndShortMonth,
    shortMonthShortDayAndYear,
} from "@similarweb/pro-logic-components";

import { translate } from "app/filters/i18nFilter";
import {
    buildCoreAverageSeries,
    hasCoreCompetitors,
} from "app/pages/digital-marketing/generative-ai/llm-tracker/reports/overview-report/shared/coreCompetitorsUtils";
import { assignDynamicLegendColors } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMTrackerUtils";
import { ALL_TOPICS_NAME } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/LLMConsts";
import type { IAiTrackerCampaign } from "app/pages/digital-marketing/generative-ai/llm-tracker/shared/types";
import type { DatesRange } from "app/pages/sales-intelligence/sub-modules/account-overview/shared/types/common";
import { GranularityNames } from "app/UtilitiesAndConstants/Constants/Moment";

import { MAX_SELECTED_BRANDS, MIN_DAYS_FOR_WEEKLY_GRANULARITY, MIN_WEEKLY_DATA_POINTS_FOR_DEFAULT } from "./consts";
import type { IBrandVisibilityBrand, IBrandVisibilityTopic, SeriesLineData, SeriesLineItem } from "./types";

const isCampaignBrand = (campaign: IAiTrackerCampaign, brandName: string): boolean => {
    return campaign.brands.some((brand) => brand.toLowerCase() === brandName.toLowerCase());
};

export const calculateDefaultGranularity = (
    data: Array<IBrandVisibilityTopic> | undefined,
    reportDays: number,
): GranularityNames => {
    if (!data || data.length === 0) {
        return GranularityNames.DAILY;
    }

    const allTopicsTopic = data.find((topic) => topic.topicName === ALL_TOPICS_NAME);
    if (!allTopicsTopic?.myBrands?.weekly) {
        return GranularityNames.DAILY;
    }

    const weeklyDataPointsCount = Object.keys(allTopicsTopic.myBrands.weekly).length;
    return reportDays >= MIN_DAYS_FOR_WEEKLY_GRANULARITY && weeklyDataPointsCount >= MIN_WEEKLY_DATA_POINTS_FOR_DEFAULT
        ? GranularityNames.WEEKLY
        : GranularityNames.DAILY;
};

export const transformData = (
    campaign: IAiTrackerCampaign,
    data: Array<IBrandVisibilityTopic>,
    granularity: GranularityNames = GranularityNames.DAILY,
    onlyCoreCompetitors = false,
): SeriesLineData => {
    const isMultiBrand = campaign.brands.length > 1;
    const myBrandDisplayName = translate(
        isMultiBrand
            ? "llm.brands-visibility.graph.legend.my_brand"
            : "llm.brands-visibility.graph.legend.my_brand.single",
        {
            brand: campaign.brands[0],
        },
    );

    return data.reduce<SeriesLineData>((acc, topic) => {
        const { topicName, brands, myBrands, coreAverage } = topic;
        if (!topicName) {
            return acc;
        }
        const myBrandObj: IBrandVisibilityBrand = myBrands && {
            isMyBrand: true,
            isCoreCompetitor: false,
            brandName: myBrandDisplayName,
            visibilityData: myBrands,
        };

        const allBrands: IBrandVisibilityBrand[] = [
            //for a campaign with a single brand - omit the brand from the list and only keep "My Brand (name)"
            ...(brands?.filter(
                (brand) =>
                    (!onlyCoreCompetitors || brand.isCoreCompetitor) &&
                    (isMultiBrand || !isCampaignBrand(campaign, brand.brandName ?? "")),
            ) ?? []),
            ...(myBrandObj ? [myBrandObj] : []),
        ].filter(Boolean);

        const brandsWithData: SeriesLineItem[] = allBrands
            .sort((a, b) => {
                if (a.isMyBrand) {
                    return -1;
                }

                return (b.visibilityData?.total ?? 0) - (a.visibilityData?.total ?? 0);
            })
            .map(({ brandName, visibilityData }, index) => {
                const dataMap: Record<string, number> =
                    granularity === GranularityNames.WEEKLY
                        ? (visibilityData?.weekly ?? {})
                        : (visibilityData?.daily ?? {});

                const result: SeriesLineItem = {
                    id: `${topicName}-${brandName}`,
                    name: brandName ?? "",
                    total: visibilityData?.total ?? 0,
                    visible: index < MAX_SELECTED_BRANDS,
                    color: "",
                    data: Object.keys(dataMap).map((date: string) => [new Date(date).getTime(), dataMap[date]]),
                };

                return result;
            });

        const coloredBrands = assignDynamicLegendColors(brandsWithData);

        const coreAverageSeries = onlyCoreCompetitors
            ? buildCoreAverageSeries(topicName, coreAverage, granularity)
            : null;

        acc[topicName] = coreAverageSeries ? [...coloredBrands, coreAverageSeries] : coloredBrands;
        return acc;
    }, {});
};

export const getTooltipTitle = (fromDate: Dayjs, granularity: GranularityNames, reportDuration: DatesRange): string => {
    switch (granularity) {
        case GranularityNames.DAILY:
            return fromDate.format(shortMonthDayAndYearWithDayOfWeek[0]);

        case GranularityNames.WEEKLY: {
            const diffFromReportEnd = reportDuration.to.endOf("day").diff(fromDate, "days");

            if (diffFromReportEnd > 0) {
                const from = fromDate.format(dayAndShortMonth);
                const to = fromDate.add(Math.min(6, diffFromReportEnd), "days").format(shortMonthShortDayAndYear);
                return `${from} - ${to}`;
            } else {
                return fromDate.format(shortMonthShortDayAndYear);
            }
        }

        default:
            return "";
    }
};
